// app/(admin)/layout.tsx
import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import LoadingIndicator from "../../components/ui/LoadingIndicator";
import Header from "../../components/layout/Header";
import Sidebar from "../../components/layout/Sidebar";
import { ROLES, ROUTES } from "../../lib/constants";
import { getMyClientConfig } from "../../lib/apiClient";

import {
  faListAlt,
  faUser,
  faObjectGroup,
  faChartBar,
  faAddressCard,
  faUserCircle,
} from "@fortawesome/free-regular-svg-icons";
import { faGear, faPercent } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export default function AdminLayout() {
  const { isAuthenticated, user, isLoading, logout } = useAuth();
  const router = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [integrationFlags, setIntegrationFlags] = useState({
    telegramEnabled: true,
    discordEnabled: true,
    slackEnabled: true,
  });

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated || user?.role !== ROLES.ADMIN) {
        console.log(
          "AdminLayout: Auth check failed or role mismatch. Redirecting to admin login."
        );
        router(ROUTES.LOGIN_ADMIN, { replace: true });
      } else {
        console.log("AdminLayout: Admin authenticated.");
      }
    }
  }, [isAuthenticated, user, isLoading, router]);

  useEffect(() => {
    const loadIntegrationFlags = async () => {
      if (!isAuthenticated || user?.role !== ROLES.ADMIN) return;

      try {
        const config = await getMyClientConfig();
        const features = config?.config?.platform?.features || {};
        setIntegrationFlags({
          telegramEnabled: features.telegramEnabled !== false,
          discordEnabled: features.discordEnabled !== false,
          slackEnabled: features.slackEnabled !== false,
        });
      } catch (error) {
        setIntegrationFlags({
          telegramEnabled: true,
          discordEnabled: true,
          slackEnabled: true,
        });
      }
    };

    loadIntegrationFlags();
  }, [isAuthenticated, user?.role]);

  useEffect(() => {
    if (!isAuthenticated || user?.role !== ROLES.ADMIN) return;

    if (
      location.pathname.startsWith(ROUTES.ADMIN_CHANNEL_NEW) &&
      !integrationFlags.telegramEnabled &&
      !integrationFlags.discordEnabled &&
      !integrationFlags.slackEnabled
    ) {
      router(ROUTES.ADMIN_CHANNELS, { replace: true });
    }
  }, [
    isAuthenticated,
    user?.role,
    location.pathname,
    integrationFlags,
    router,
  ]);

  const adminNavSections = [
    {
      label: "Overview",
      items: [
        {
          href: ROUTES.ADMIN_DASHBOARD,
          label: "Dashboard",
          icon: <FontAwesomeIcon icon={faChartBar} />,
          isActiveCheck: (pn: string, hr: string) => pn === hr,
        },
      ],
    },
    {
      label: "Management",
      items: [
        {
          href: ROUTES.ADMIN_CHANNELS,
          label: "My Channels",
          icon: <FontAwesomeIcon icon={faListAlt} />,
          isActiveCheck: (pn: string, hr: string) => pn.startsWith(hr),
        },
        {
          href: ROUTES.ADMIN_COMBOS,
          label: "Combo Plans",
          icon: <FontAwesomeIcon icon={faObjectGroup} />,
          isActiveCheck: (pn: string, hr: string) => pn.startsWith(hr),
        },
        {
          href: ROUTES.ADMIN_LEADS,
          label: "Channel Leads",
          icon: <FontAwesomeIcon icon={faAddressCard} />,
          isActiveCheck: (pn: string, hr: string) => pn.startsWith(hr),
        },
        {
          href: "/admin/manual-add",
          label: "Manual Addition",
          icon: <FontAwesomeIcon icon={faUserCircle} />,
          isActiveCheck: (pn: string, hr: string) => pn.startsWith(hr),
        },
        {
          href: ROUTES.ADMIN_COUPONS,
          label: "Universal Coupon",
          icon: <FontAwesomeIcon icon={faPercent} />,
          isActiveCheck: (pn: string, hr: string) => pn.startsWith(hr),
        },
      ],
    },
    {
      label: "Admin Configs",
      items: [
        {
          href: ROUTES.ADMIN_PROFILE,
          label: "My Profile",
          icon: <FontAwesomeIcon icon={faUser} />,
          isActiveCheck: (pn: string, hr: string) => pn.startsWith(hr),
        },
        {
          href: ROUTES.ADMIN_MY_TEAM,
          label: "My Team",
          icon: <FontAwesomeIcon icon={faAddressCard} />,
          isActiveCheck: (pn: string, hr: string) => pn.startsWith(hr),
        },
        {
          href: ROUTES.ADMIN_SETTINGS,
          label: "Settings",
          icon: <FontAwesomeIcon icon={faGear} />,
          isActiveCheck: (pn: string, hr: string) => pn.startsWith(hr),
        },
      ],
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-dark-primary">
        <LoadingIndicator size="lg" text="Verifying Admin access..." />
      </div>
    );
  }

  if (!isLoading && isAuthenticated && user?.role === ROLES.ADMIN) {
    return (
      <div className="min-h-screen flex bg-dark-primary">
        <Sidebar
          title="Admin Panel"
          collapsedIcon={<span className="text-2xl font-bold">A</span>}
          headerLink={ROUTES.ADMIN_DASHBOARD}
          sections={adminNavSections}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onLinkClick={() => setIsSidebarOpen(false)}
        />

        <div className="flex-1 flex flex-col overflow-hidden">
          <Header
            title="SuBs"
            titleLink={ROUTES.ADMIN_DASHBOARD}
            onOpenSidebar={() => setIsSidebarOpen(true)}
            isAuthenticated={isAuthenticated}
            onLogout={logout}
            profileRoute={ROUTES.ADMIN_PROFILE} // Pass the Admin profile route
          />

          <main className="flex-grow p-4 md:p-6 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-dark-primary">
      <LoadingIndicator size="lg" text="Redirecting..." />
    </div>
  );
}
